/**
 * Outline View Component
 * Hierarchical tree view of all elements in the document with Row/Cell structure
 */

import { ChevronRight, ChevronDown, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import type { ContainerElement, TemplateElement, AbsoluteElement } from '../../types/template.types';

interface OutlineViewProps {
  headerContainer: ContainerElement;
  footerContainer: ContainerElement;
  rootContainer: ContainerElement | ContainerElement[];
  absoluteElements: AbsoluteElement[];
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onDeleteElement?: (elementId: string) => void;
  onDeleteRow?: (containerId: string, rowIndex: number) => void;
}

interface RowNodeProps {
  containerId: string;
  rowIndex: number;
  columns: number;
  children: ContainerElement['children'];
  level: number;
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onDeleteElement?: (elementId: string) => void;
  onDeleteRow?: (containerId: string, rowIndex: number) => void;
}

interface CellNodeProps {
  containerId: string;
  rowIndex: number;
  columnIndex: number;
  children: ContainerElement['children'];
  level: number;
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onDeleteElement?: (elementId: string) => void;
}

function CellNode({ 
  containerId, 
  rowIndex, 
  columnIndex, 
  children, 
  level, 
  selectedElementId, 
  onSelectElement, 
  onDeleteElement 
}: CellNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const cellElements = children.filter(child => child.cellRow === rowIndex && child.cellColumn === columnIndex);
  const hasChildren = cellElements.length > 0;

  return (
    <div>
      <div
        className="flex items-center gap-1 px-2 py-1 cursor-pointer hover:bg-muted"
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="w-4 h-4 flex items-center justify-center hover:bg-accent rounded"
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        )}
        {!hasChildren && <span className="w-4" />}
        <span className="text-sm text-muted-foreground">Cell {columnIndex}</span>
      </div>
      
      {hasChildren && isExpanded && (
        <div>
          {cellElements.map((child) => (
            <ElementNode
              key={child.element.id}
              element={child.element}
              level={level + 1}
              selectedElementId={selectedElementId}
              onSelectElement={onSelectElement}
              onDeleteElement={onDeleteElement}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RowNode({ 
  containerId, 
  rowIndex, 
  columns, 
  children, 
  level, 
  selectedElementId, 
  onSelectElement, 
  onDeleteElement, 
  onDeleteRow 
}: RowNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  
  const rowElements = children.filter(child => child.cellRow === rowIndex);
  const hasChildren = rowElements.length > 0;

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDeleteRow) {
      onDeleteRow(containerId, rowIndex);
    }
    setShowContextMenu(false);
  };

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => setShowContextMenu(false);
    if (showContextMenu) {
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [showContextMenu]);

  return (
    <div>
      <div
        className="flex items-center gap-1 px-2 py-1 cursor-pointer hover:bg-muted"
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => setIsExpanded(!isExpanded)}
        onContextMenu={handleContextMenu}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="w-4 h-4 flex items-center justify-center hover:bg-accent rounded"
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        )}
        {!hasChildren && <span className="w-4" />}
        <span className="text-sm text-muted-foreground">Row {rowIndex}</span>
      </div>
      
      {/* Context Menu */}
      {showContextMenu && (
        <div
          className="fixed bg-popover border shadow-lg rounded py-1 z-50"
          style={{ left: contextMenuPos.x, top: contextMenuPos.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleDelete}
            className="w-full px-4 py-2 text-left text-sm hover:bg-accent flex items-center gap-2 text-destructive"
          >
            <Trash2 className="w-4 h-4" />
            Row löschen
          </button>
        </div>
      )}
      
      {isExpanded && (
        <div>
          {Array.from({ length: columns }, (_, columnIndex) => (
            <CellNode
              key={`${rowIndex}-${columnIndex}`}
              containerId={containerId}
              rowIndex={rowIndex}
              columnIndex={columnIndex}
              children={children}
              level={level + 1}
              selectedElementId={selectedElementId}
              onSelectElement={onSelectElement}
              onDeleteElement={onDeleteElement}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ElementNode({ 
  element, 
  level, 
  selectedElementId, 
  onSelectElement, 
  onDeleteElement 
}: { 
  element: TemplateElement; 
  level: number; 
  selectedElementId: string | null; 
  onSelectElement: (id: string | null) => void; 
  onDeleteElement?: (elementId: string) => void;
}) {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const isSelected = selectedElementId === element.id;

  const getElementLabel = () => {
    const typeLabels: Record<string, string> = {
      'text': 'Text',
      'image': 'Bild',
      'sender': 'Absender',
      'recipient': 'Empfänger',
      'invoice-number': 'Rechnungsnummer',
      'date': 'Datum',
      'bank-name': 'Bankname',
      'iban': 'IBAN',
      'bic': 'BIC',
      'account-holder': 'Kontoinhaber',
      'absolute-image': 'Absolutes Bild',
    };
    
    return typeLabels[element.type] || element.type;
  };

  const handleClick = () => {
    onSelectElement(element.id);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDeleteElement) {
      onDeleteElement(element.id);
    }
    setShowContextMenu(false);
  };

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => setShowContextMenu(false);
    if (showContextMenu) {
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [showContextMenu]);

  return (
    <div>
      <div
        className={`flex items-center gap-1 px-2 py-1 cursor-pointer hover:bg-muted ${
          isSelected ? 'bg-accent text-accent-foreground' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        <span className="w-4" />
        <span className="text-sm truncate">{getElementLabel()}</span>
      </div>
      
      {/* Context Menu */}
      {showContextMenu && (
        <div
          className="fixed bg-popover border shadow-lg rounded py-1 z-50"
          style={{ left: contextMenuPos.x, top: contextMenuPos.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleDelete}
            className="w-full px-4 py-2 text-left text-sm hover:bg-accent flex items-center gap-2 text-destructive"
          >
            <Trash2 className="w-4 h-4" />
            Löschen
          </button>
        </div>
      )}
    </div>
  );
}



function AbsoluteElementNode({ 
  element, 
  selectedElementId, 
  onSelectElement, 
  onDeleteElement 
}: { 
  element: AbsoluteElement; 
  selectedElementId: string | null; 
  onSelectElement: (id: string | null) => void; 
  onDeleteElement?: (elementId: string) => void;
}) {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const isSelected = selectedElementId === element.id;

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDeleteElement) {
      onDeleteElement(element.id);
    }
    setShowContextMenu(false);
  };

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => setShowContextMenu(false);
    if (showContextMenu) {
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [showContextMenu]);

  return (
    <div>
      <div
        className={`flex items-center gap-1 px-2 py-1 cursor-pointer hover:bg-muted ${
          isSelected ? 'bg-accent text-accent-foreground' : ''
        }`}
        style={{ paddingLeft: '24px' }}
        onClick={() => onSelectElement(element.id)}
        onContextMenu={handleContextMenu}
      >
        <span className="w-4" />
        <span className="text-sm truncate">Absolutes Bild</span>
      </div>
      
      {/* Context Menu */}
      {showContextMenu && (
        <div
          className="fixed bg-popover border shadow-lg rounded py-1 z-50"
          style={{ left: contextMenuPos.x, top: contextMenuPos.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleDelete}
            className="w-full px-4 py-2 text-left text-sm hover:bg-accent flex items-center gap-2 text-destructive"
          >
            <Trash2 className="w-4 h-4" />
            Löschen
          </button>
        </div>
      )}
    </div>
  );
}

export function OutlineView({
  headerContainer,
  footerContainer,
  rootContainer,
  absoluteElements,
  selectedElementId,
  onSelectElement,
  onDeleteElement,
  onDeleteRow,
}: OutlineViewProps) {
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    root: true,
    footer: true,
    absolute: true,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const rootContainers = rootContainer 
    ? (Array.isArray(rootContainer) ? rootContainer : [rootContainer])
    : [];

function ContainerWithRows({ 
  container, 
  level, 
  selectedElementId, 
  onSelectElement, 
  onDeleteElement, 
  onDeleteRow 
}: { 
  container: ContainerElement; 
  level: number; 
  selectedElementId: string | null; 
  onSelectElement: (id: string | null) => void; 
  onDeleteElement?: (elementId: string) => void; 
  onDeleteRow?: (containerId: string, rowIndex: number) => void; 
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = container.children?.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-1 px-2 py-1 cursor-pointer hover:bg-muted ${
          selectedElementId === container.id ? 'bg-accent text-accent-foreground' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelectElement(container.id)}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="w-4 h-4 flex items-center justify-center hover:bg-accent rounded"
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        )}
        {!hasChildren && <span className="w-4" />}
        <span className="text-sm truncate">
          Container ({container.rows}×{container.columns})
        </span>
      </div>
      
      {hasChildren && isExpanded && (
        <div>
          {Array.from({ length: container.rows }, (_, rowIndex) => (
            <RowNode
              key={`${container.id}-row-${rowIndex}`}
              containerId={container.id}
              rowIndex={rowIndex}
              columns={container.columns}
              children={container.children}
              level={level + 1}
              selectedElementId={selectedElementId}
              onSelectElement={onSelectElement}
              onDeleteElement={onDeleteElement}
              onDeleteRow={onDeleteRow}
            />
          ))}
        </div>
      )}
    </div>
  );
}

  return (
    <div className="h-full flex flex-col bg-card border-l border-border">
      <div className="p-3 border-b border-border">
        <h2 className="text-sm font-semibold text-foreground">Outline</h2>
      </div>
      
      <div className="flex-1 overflow-y-auto text-sm">
        {/* Header Section */}
        {headerContainer && (
          <div>
            <button
              onClick={() => toggleSection('header')}
              className="w-full flex items-center gap-1 px-2 py-2 hover:bg-muted font-medium text-foreground"
            >
              {expandedSections.header ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span>Header</span>
            </button>
            {expandedSections.header && (
              <ContainerWithRows
                container={headerContainer}
                level={0}
                selectedElementId={selectedElementId}
                onSelectElement={onSelectElement}
                onDeleteElement={onDeleteElement}
                onDeleteRow={onDeleteRow}
              />
            )}
          </div>
        )}

        {/* Root Section */}
        {rootContainers.length > 0 && (
          <div>
            <button
              onClick={() => toggleSection('root')}
              className="w-full flex items-center gap-1 px-2 py-2 hover:bg-muted font-medium text-foreground"
            >
              {expandedSections.root ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span>Root</span>
            </button>
            {expandedSections.root && (
              <div>
                {rootContainers.map((container) => (
                  <ContainerWithRows
                    key={container.id}
                    container={container}
                    level={0}
                    selectedElementId={selectedElementId}
                    onSelectElement={onSelectElement}
                    onDeleteElement={onDeleteElement}
                    onDeleteRow={onDeleteRow}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer Section */}
        {footerContainer && (
          <div>
            <button
              onClick={() => toggleSection('footer')}
              className="w-full flex items-center gap-1 px-2 py-2 hover:bg-muted font-medium text-foreground"
            >
              {expandedSections.footer ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span>Footer</span>
            </button>
            {expandedSections.footer && (
              <ContainerWithRows
                container={footerContainer}
                level={0}
                selectedElementId={selectedElementId}
                onSelectElement={onSelectElement}
                onDeleteElement={onDeleteElement}
                onDeleteRow={onDeleteRow}
              />
            )}
          </div>
        )}

        {/* Absolute Elements Section */}
        {absoluteElements.length > 0 && (
          <div>
            <button
              onClick={() => toggleSection('absolute')}
              className="w-full flex items-center gap-1 px-2 py-2 hover:bg-muted font-medium text-foreground"
            >
              {expandedSections.absolute ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span>Absolute Elemente</span>
            </button>
            {expandedSections.absolute && (
              <div>
                {absoluteElements.map((element) => (
                  <AbsoluteElementNode
                    key={element.id}
                    element={element}
                    selectedElementId={selectedElementId}
                    onSelectElement={onSelectElement}
                    onDeleteElement={onDeleteElement}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
